/**
 * Login Page Component
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Shield, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { AppBrandLogo } from '../components/AppBrandLogo';
import AuthSplitShell from '../components/marketing/AuthSplitShell';
import { IMAGES } from '../lib/marketingImages';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, BACKEND_URL } from '../config';
import { GoogleOAuthSection } from '../components/OAuthButtons';
import { useNoIndex } from '../lib/useNoIndex';


const LoginPage = () => {
  useNoIndex();
  const [identifier, setIdentifier] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleOAuthSuccess = (data) => {
    const userData = login(data.access_token, data.user);
    toast.success("Signed in successfully!");
    if (data.needs_pin_setup || userData?.needs_pin_setup) {
      navigate("/setup-pin");
    } else {
      navigate(userData?.is_admin ? "/admin" : "/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      toast.error("Please enter your email or phone number");
      return;
    }
    
    if (!pin || pin.length < 4) {
      toast.error("Please enter your PIN (4-6 digits)");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { identifier, pin });
      login(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      navigate(response.data.user.is_admin ? "/admin" : "/");
    } catch (error) {
      if (!error.response) {
        toast.error(
          `Cannot reach API at ${BACKEND_URL}. Start the backend on port 8001, then try again.`
        );
      } else {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell
      image={IMAGES.livingRoom}
      kicker="Welcome back"
      headline="Your home is one PIN away from staying on."
      caption="Pay electricity, water, and TV without leaving the sofa."
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        <div className="text-center mb-8 lg:hidden">
          <AppBrandLogo banner className="mb-4" />
        </div>
        <p className="text-muted-foreground mb-6 text-center lg:text-left">Sign in to pay a bill in seconds.</p>

        {/* Login Card */}
        <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-xl" data-testid="login-card">
          <h2 className="text-xl font-semibold mb-6">Welcome back</h2>
          
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email/Phone Input */}
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">Email or Phone Number</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="email@example.com or 0991234567"
                  className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-base"
                  data-testid="identifier-input"
                />
              </div>
            </div>

            {/* PIN Input */}
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">PIN</label>
              <div className="relative">
                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter your PIN"
                  className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-base tracking-widest"
                  data-testid="pin-input"
                />
              </div>
            </div>

            {/* Forgot PIN Link */}
            <div className="text-right">
              <button type="button" onClick={() => navigate("/forgot-pin")} className="text-sm text-primary hover:underline" data-testid="forgot-pin-link">
                Forgot PIN?
              </button>
            </div>

            {/* Login Button */}
            <button type="submit" disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" data-testid="login-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Login <ChevronRight className="w-5 h-5" /></>}
            </button>
          </form>

          {/* OAuth */}
          <GoogleOAuthSection onSuccess={handleOAuthSuccess} disabled={loading} />

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Create Account Button */}
          <button onClick={() => navigate("/register")} className="w-full py-4 bg-secondary text-foreground border border-border rounded-xl font-semibold hover:bg-secondary/80 transition-all flex items-center justify-center gap-2" data-testid="create-account-btn">
            <Plus className="w-5 h-5" /> Create Account
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Secure payments powered by PayChangu
        </p>
      </motion.div>
    </AuthSplitShell>
  );
};

export default LoginPage;